'use client';

import React, { useEffect, useState, Suspense } from 'react';
import { DndContext, closestCenter, PointerSensor, useSensor, useSensors } from '@dnd-kit/core';
import { SortableContext, verticalListSortingStrategy, arrayMove } from '@dnd-kit/sortable';
import { Button, EmptyScreen } from '@ui/components';
import { PlusIcon, Lock, Link as LinkIcon, ExternalLink } from 'lucide-react';
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@ui/components/ui/tooltip';
import { useRouter } from 'next/navigation';
import { useClickOutside } from '@ui/hooks';
import { IConversationSet, useConversationContext } from '@ui/components/organisms/conversation';
import { ContextUserType } from '@ui/components/organisms/message';
import { SortableItem } from './sortable-item';
import { DynamicRouteBuilder } from '@ui/constants';
import { useParams } from 'next/navigation';

const CreateTopicLinkModal = React.lazy(() => import('../modals/CreateTopicLinkModal'));
const ExternalLinkConfirmModal = React.lazy(() => import('../modals/ExternalLinkConfirmModal'));

export interface TopicListItemProps {
  topics: IConversationSet[];
  userType: ContextUserType;
  conversationState?: ReturnType<typeof useConversationContext>;
}

export const TopicListItem: React.FC<TopicListItemProps> = ({
  topics,
  userType,
  conversationState,
}) => {
  const [localTopics, setLocalTopics] = useState<IConversationSet[]>([]);
  const [openModal, setOpenModal] = useState(false);
  const [modalMode, setModalMode] = useState<'topic' | 'link'>('topic');
  const [openLinkConfirmation, setOpenLinkConfirmation] = useState(false);
  const [pendingLinkUrl, setPendingLinkUrl] = useState<string | null>(null);
  const [editingConversationId, setEditingConversationId] = useState<string | null>(null);
  const [editInitialName, setEditInitialName] = useState<string>('');
  const [editInitialDescription, setEditInitialDescription] = useState<string>('');
  const [editInitialLinkUrl, setEditInitialLinkUrl] = useState<string>('');
  const linkConfirmationRef = useClickOutside(() => setOpenLinkConfirmation(false));
  const router = useRouter();
  const params = useParams();

  const sensors = useSensors(
    useSensor(PointerSensor, {
      activationConstraint: { distance: 5 },
    }),
  );

  useEffect(() => {
    setLocalTopics(topics);
  }, [topics]);

  const handleDragEnd = async (event: any) => {
    const { active, over } = event;
    if (!over || active.id === over.id) return;

    const oldIndex = localTopics.findIndex((t) => t.conversation.id === active.id);
    const newIndex = localTopics.findIndex((t) => t.conversation.id === over.id);

    const reordered = arrayMove(localTopics, oldIndex, newIndex);
    setLocalTopics(reordered);

    if (conversationState?.creator_id && userType === 'creator') {
      await conversationState.onReorderConversations({
        creator_id: conversationState.creator_id,
        conversation_type: 'topic',
        ordered_ids: reordered.map((t) => t.conversation.id),
      });
    }
  };

  const handleLinkClick = (url: string, isCreator: boolean) => {
    if (isCreator) {
      // window.location.href = url;
      window.location.href = url;
    } else {
      setPendingLinkUrl(url);
      setOpenLinkConfirmation(true);
    }
  };

  const handleConfirmLink = () => {
    if (pendingLinkUrl) {
      // window.open(pendingLinkUrl, '_blank');
      window.location.href = pendingLinkUrl;
      setOpenLinkConfirmation(false);
      setPendingLinkUrl(null);
    }
  };

  const handleCancelLink = () => {
    setOpenLinkConfirmation(false);
    setPendingLinkUrl(null);
  };

  const handleCreate = async (payload: { name: string; description: string; linkUrl?: string }) => {
    await conversationState?.onCreateConversation({
      creator_id: conversationState?.creator_id,
      name: payload.name,
      description: payload.description,
      conversation_type: 'topic',
      restrictions: {
        fans_react: true,
        fans_reply: true,
        fans_report: true,
      },
      public: true,
      ready: true,
      ...(payload.linkUrl ? { miscellaneous: { link_url: payload.linkUrl } } : {}),
    });
    conversationState?.onRefetchTopic?.();
  };

  const handleUpdate = async (
    conversationId: string,
    payload: { name: string; description: string; linkUrl?: string },
  ) => {
    await conversationState?.onUpdate({
      id: conversationId,
      name: payload.name,
      description: payload.description,
      ...(payload.linkUrl ? { miscellaneous: { link_url: payload.linkUrl } } : {}),
      params: { creator_id: conversationState?.creator_id } as any,
    } as any);
    conversationState?.onRefetchTopic?.();
  };

  const renderTopics = () => {
    if (!localTopics.length) {
      return <EmptyScreen title="Empty" message="No Topics have been created by this creator" />;
    }

    if (userType === 'creator') {
      return (
        <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
          <SortableContext
            items={localTopics.map((t) => t.conversation.id)}
            strategy={verticalListSortingStrategy}>
            <div className="grid gap-4 grid-cols-1 mt-4 relative">
              {localTopics.map((set) => {
                const isLink = !!set.conversation.miscellaneous?.link_url;
                return (
                  <SortableItem
                    key={set.conversation.id}
                    id={set.conversation.id}
                    title={set.conversation.name}
                    description={set.conversation.description}
                    public={set.conversation.public}
                    isLink={isLink}
                    linkUrl={set.conversation.miscellaneous?.link_url}
                    onEditLink={(e) => {
                      e.stopPropagation();
                      setEditingConversationId(set.conversation.id);
                      setEditInitialName(set.conversation.name ?? '');
                      setEditInitialDescription(set.conversation.description ?? '');
                      setEditInitialLinkUrl(set.conversation.miscellaneous?.link_url ?? '');
                      setModalMode('link');
                      setOpenModal(true);
                    }}
                    onDeleteTopic={conversationState?.onDeleteTopic}
                    isDeleteSuccess={conversationState?.isDeletionConversationSuccess}
                    isDeleting={conversationState?.isDeletingConversation}
                    onClick={() => {
                      router.push(
                        `/dashboard/community/topic?conversation_id=${set.conversation.id}`,
                      );
                    }}
                  />
                );
              })}
            </div>
          </SortableContext>
        </DndContext>
      );
    } else {
      return (
        <div className="grid gap-4 grid-cols-1 mt-4">
          {localTopics.map((set) => {
            const isLink = !!set.conversation.miscellaneous?.link_url;
            return (
              <div
                key={set.conversation.id}
                className={`border rounded-md p-4 shadow-sm hover:shadow transition cursor-pointer relative ${
                  isLink ? 'min-h-[8rem] bg-blue-50' : 'min-h-[12rem]'
                }`}
                onClick={() => {
                  if (isLink && set.conversation.miscellaneous?.link_url) {
                    handleLinkClick(set.conversation.miscellaneous.link_url, false);
                  } else {
                    router.push(
                      DynamicRouteBuilder.topicBySlug(
                        (params.creatorSlug as string) ?? set.conversation.created_at,
                        set?.conversation?.slug ?? set?.conversation?.id,
                      ),
                    );
                  }
                }}>
                <div className="flex items-center justify-between">
                  <h3 className="font-semibold text-base truncate">
                    {set.conversation.name ?? 'Untitled'}
                  </h3>
                  <div className="flex items-center gap-2">
                    {!set.conversation.public && (
                      <TooltipProvider>
                        <Tooltip>
                          <TooltipTrigger asChild>
                            <div onClick={(e) => e.stopPropagation()}>
                              <Lock className="w-4 h-4 text-gray-500 flex-shrink-0 cursor-help" />
                            </div>
                          </TooltipTrigger>
                          <TooltipContent>
                            <p>Private Topic</p>
                          </TooltipContent>
                        </Tooltip>
                      </TooltipProvider>
                    )}
                  </div>
                </div>
                {set.conversation.description && (
                  <p className="text-sm text-gray-600 line-clamp-3 mt-1">
                    {set.conversation.description}
                  </p>
                )}
                {isLink && (
                  <div className="absolute bottom-4 right-4">
                    <ExternalLink className="w-4 h-4 text-gray-500 flex-shrink-0" />
                  </div>
                )}
              </div>
            );
          })}
        </div>
      );
    }
  };

  return (
    <div className="flex flex-col">
      {userType === 'creator' && (
        <div className="flex justify-end mb-2 gap-2">
          <Button
            type="primary"
            size="small"
            className="flex items-center gap-2 rounded-md"
            onClick={() => {
              setModalMode('topic');
              setOpenModal(true);
            }}>
            <PlusIcon className="w-4 h-4" />
            Create New Topic
          </Button>
          <Button
            type="secondary"
            size="small"
            className="flex items-center gap-2 rounded-md bg-black text-white"
            onClick={() => {
              setModalMode('link');
              setOpenModal(true);
            }}>
            <LinkIcon className="w-4 h-4" />
            Add Link
          </Button>
        </div>
      )}

      {renderTopics()}

      {openModal && (
        <Suspense fallback={null}>
          <CreateTopicLinkModal
            open={openModal}
            mode={modalMode}
            onClose={() => setOpenModal(false)}
            onSubmit={async (data: { name: string; description: string; linkUrl?: string }) => {
              if (editingConversationId) {
                await handleUpdate(editingConversationId, data);
              } else {
                await handleCreate(data);
              }
              setEditingConversationId(null);
              setOpenModal(false);
            }}
            initialName={editingConversationId ? editInitialName : undefined}
            initialDescription={editingConversationId ? editInitialDescription : undefined}
            initialLinkUrl={editingConversationId ? editInitialLinkUrl : undefined}
          />
        </Suspense>
      )}

      {openLinkConfirmation && (
        <Suspense fallback={null}>
          <ExternalLinkConfirmModal
            open={openLinkConfirmation}
            onCancel={handleCancelLink}
            onConfirm={handleConfirmLink}
          />
        </Suspense>
      )}
    </div>
  );
};
